<template>
  <div>
    <el-form
      ref="editForm"
      v-loading="isLoading"
      :rules="rules"
      :model="account"
      label-position="top"
      size="small"
    >
      <el-row :gutter="10">
        <el-col :md="12">
          <el-form-item
            label="Trading Status"
            prop="status"
          >
            <el-select
              v-model="account.status"
              placeholder="Trading Status"
              class="w-100"
            >
              <el-option
                label="Pending"
                value="pending"
                disabled
              />
              <el-option
                v-for="status in statuses"
                :key="status"
                :label="$filters.titleize(status)"
                :value="status"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="12">
          <el-form-item
            label="Status"
            prop="endangered_status"
          >
            <el-select
              v-model="account.crm_endangered"
              placeholder="Account Status"
              class="w-100"
            >
              <el-option
                v-for="endangered_status in endangered_statuses"
                :key="endangered_status.value"
                :label="$filters.titleize(endangered_status.label)"
                :value="endangered_status.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item
        label="Name"
        prop="name"
      >
        <el-input
          v-model="account.name"
          placeholder="Name"
          clearable
        />
      </el-form-item>
      <el-form-item
        label="Address"
        prop="address"
      >
        <el-input
          v-model="account.address"
          placeholder="Address"
          clearable
        />
      </el-form-item>
      <el-row :gutter="10">
        <el-col :md="8">
          <el-form-item
            label="Suburb"
            prop="suburb"
          >
            <el-autocomplete
              v-model="account.suburb"
              :fetch-suggestions="suburbQuerySearch"
              placeholder="Enter name of suburb"
              :trigger-on-focus="false"
              class="w-100"
              placement="top"
              :debounce="500"
              clearable
              @select="handleSelectSuburb"
            >
              <template v-slot="{ item }">
                {{ formatSuburbByLocation(item) }}
              </template>
            </el-autocomplete>
          </el-form-item>
        </el-col>
        <el-col :md="8">
          <el-form-item
            label="State"
            prop="state"
          >
            <el-input
              v-model="account.state"
              placeholder="E.g. Victoria"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="8">
          <el-form-item
            label="Postcode"
            prop="postcode"
          >
            <el-input
              v-model="account.postcode"
              placeholder="E.g. 3000"
              clearable
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :md="6">
          <el-form-item label="Contact">
            <el-input
              v-model="account.contact"
              placeholder="Contact"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="6">
          <el-form-item label="Phone">
            <el-input
              v-model="account.phone"
              placeholder="Phone"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="6">
          <el-form-item label="Fax">
            <el-input
              v-model="account.fax"
              placeholder="Fax"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="6">
          <el-form-item
            label="Email"
            prop="email"
            :rules="[{ type: 'email', message: 'Please input correct email address', trigger: ['blur', 'change'] }]"
          >
            <el-input
              v-model="account.email"
              placeholder="Email"
              clearable
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :md="12">
          <el-form-item label="Parent">
            <el-autocomplete
              v-model="parentInput"
              class="w-100"
              :fetch-suggestions="querySearchParentAccount"
              placeholder="Parent Account"
              clearable
              value-key="name"
              @select="handleSelectParent"
              @clear="clearParentInput"
            />
          </el-form-item>
        </el-col>
        <el-col :md="12">
          <el-form-item label="Billing Group">
            <el-select
              v-model="account.b_group"
              :loading="!bGroups"
              value-key="id"
              placeholder="Billing Group"
              class="w-100"
              clearable
            >
              <el-option
                v-for="bgroup in bGroups"
                :key="bgroup.id"
                :label="bgroup.long"
                :value="bgroup"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :md="6">
          <el-form-item label="Portfolio">
            <el-select
              v-model="account.portfolio"
              :loading="!portfolios"
              value-key="id"
              placeholder="Portfolio"
              class="w-100"
              clearable
            >
              <el-option
                v-for="portfolio in portfolios"
                :key="portfolio.id"
                :label="portfolio.long"
                :value="portfolio"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="6">
          <el-form-item label="Investigator">
            <el-select
              v-model="account.investigator"
              :loading="!investigators"
              value-key="id"
              placeholder="Investigator"
              class="w-100"
              clearable
            >
              <el-option
                v-for="investigator in investigators"
                :key="investigator.id"
                :label="investigator.name_full"
                :value="investigator"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="4">
          <el-form-item label="Freight Advisor">
            <el-select
              v-model="account.advisor"
              :loading="!advisors"
              value-key="id"
              placeholder="Freight Advisor"
              class="w-100"
              clearable
            >
              <el-option
                v-for="advisor in advisors"
                :key="advisor.id"
                :label="advisor.name_full"
                :value="advisor"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="4">
          <el-form-item label="Account Type">
            <el-select
              v-model="account.acc_type"
              :loading="!accTypes"
              value-key="id"
              placeholder="Account Type"
              class="w-100"
              clearable
            >
              <el-option
                v-for="accType in accTypes"
                :key="accType.id"
                :label="accType.desc"
                :value="accType"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="4">
          <el-form-item label="Account Grade">
            <el-select
              v-model="account.acc_grade"
              :loading="!accGrades"
              value-key="id"
              placeholder="Account Grade"
              class="w-100"
              clearable
            >
              <el-option
                v-for="accGrade in accGrades"
                :key="accGrade.id"
                :label="accGrade.name"
                :value="accGrade"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :md="12">
          <el-form-item label="Stationary Type">
            <el-select
              v-model="account.stationary_type"
              :loading="!stationaryTypes"
              value-key="id"
              placeholder="Stationary Type"
              class="w-100"
              clearable
            >
              <el-option
                v-for="stationaryType in stationaryTypes"
                :key="stationaryType.id"
                :label="stationaryType.name"
                :value="stationaryType"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="4">
          <el-form-item label="Contact Weeks">
            <el-input-number
              v-model="account.crm_contact_weeks"
              placeholder="Contact Weeks"
              controls-position="right"
            />
          </el-form-item>
        </el-col>
        <el-col :md="4">
          <el-form-item label="Include In Daily Download">
            <el-form-item label="">
              <el-switch v-model="account.daily_download" />
            </el-form-item>
          </el-form-item>
        </el-col>
        <el-col :md="4">
          <el-form-item label="Enable Tracking Monitoring">
            <el-form-item label="">
              <el-switch v-model="account.monitor_tracking" />
            </el-form-item>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :md="12">
          <el-col :md="8">
            <el-form-item label="SSO Status">
              <el-select
                v-model="account.sso_status"
                value-key="id"
                placeholder="SSO Status"
                class="w-100"
                clearable
              >
                <el-option
                  v-for="ssoStatus in sso_statuses"
                  :key="ssoStatus.value"
                  :label="ssoStatus.label"
                  :value="ssoStatus.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :md="8">
            <el-form-item label="SSO Code">
              <el-input
                v-model="account.sso_code"
                placeholder="Not Configured"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :md="8">
            <el-form-item label="Configuration Link">
              <el-button
                v-loading="isGeneratingSSOLink"
                type="primary"
                @click="generateSSOLink"
              >
                Generate Link
              </el-button>
            </el-form-item>
          </el-col>
        </el-col>
        <el-col :md="6">
          <el-form-item label="Open360 Account Code">
            <el-input
              v-model="account.slug"
              placeholder="Not Configured"
              clearable
              :disabled="!canSetSlug"
            />
          </el-form-item>
        </el-col>
        <el-col :md="6">
          <el-form-item label="Freshdesk ID">
            <el-input
              v-model="account.freshdesk_object_id"
              placeholder="Not Configured"
              clearable
              :disabled="true"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import Request, { POST } from '@/plugins/sdk/Request'

export default {
  props: {
    accountId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isLoading: false,
      account: {},
      bGroups: [],
      portfolios: [],
      investigators: [],
      advisors: [],
      accTypes: [],
      accGrades: [],
      stationaryTypes: [],
      parentInput: '',
      statuses: ['trading', 'on hold', 'closed'],
      endangered_statuses: [
        { label: 'New', value: -1 },
        { label: 'Healthy', value: 0 },
        { label: 'Endangered', value: 1 }
      ],
      sso_statuses: [
        { label: 'Active', value: 1 },
        { label: 'Disabled', value: 0 },
        { label: 'Pending', value: 2 }
      ],
      sso_url: '',
      isGeneratingSSOLink: false,
      rules: {
        name: [
          { required: true, message: 'Name of the account is required', trigger: 'blur' }
        ],
        address: [
          { required: true, message: 'Address of the account is required', trigger: 'blur' }
        ],
        suburb: [
          { required: true, message: 'Please enter and select a suburb from the list', trigger: ['blur', 'change'] }
        ],
        state: [
          { required: true, message: 'State of the address is required', trigger: ['blur', 'change'] }
        ],
        postcode: [
          { required: true, message: 'Postcode of the address is required', trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  created () {
    this.readAccount()
    this.loadLists()
  },
  methods: {
    canSetSlug () {
      return this.account.parent === null
    },
    async generateSSOLink () {
      // TODO: Implement SSO Link Generation
      // await this.saveAccountEdit()
      if (!this.account.sso_code) {
        this.$message({ message: 'SSO Code is required', type: 'warning' })
      } else {
        this.isGeneratingSSOLink = true
        const request = await (Object.assign(new Request(), {
          method: POST,
          _pathname: 'account/' + this.accountId + '/generatesso',
          body: {
            sso_code: this.account.sso_code
          }
        })).build()

        const response = await fetch(request)

        if (response.ok) {
          let data = await response.json()
          this.sso_url = window.location.origin + '/#/authentication/configure/sso?code=' + data.code
          this.$alert(this.sso_url, 'One Time SSO Link', {
            confirmButtonText: 'Copy & Close',
            callback: action => {
              if (action === 'confirm') {
                navigator.clipboard.writeText(this.sso_url)
                this.$message({ message: 'SSO Link copied to clipboard', type: 'success' })
              }
            }
          })
        } else {
          this.$message({ message: 'Failed to generate SSO Link', type: 'error' })
        }
        this.isGeneratingSSOLink = false
      }
    },
    readAccount () {
      this.isLoading = true
      this.$sdk.account.read(this.accountId, (builder) => {
        builder.include = ['acc_type', 'parent', 'b_group', 'portfolio.profile', 'stationary_type', 'investigator', 'advisor', 'acc_grade']
      })
        .then(acc => {
          this.account = acc
          this.parentInput = acc.parent ? acc.parent.name : ''
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    loadLists () {
      // BGROUP
      this.$sdk.dictionary_bgroup.list(builder => {
        builder.page = 1
        builder.limit = 1000
        builder.order = 'uuid:asc'
      }).then(res => {
        this.bGroups = res
      })
      // PORTFOLIO
      this.$sdk.dictionary_portfolio.list(builder => {
        builder.page = 1
        builder.limit = 1000
        builder.order = 'portfolio_long:asc'
      }).then(res => {
        this.portfolios = res
      })
      // Investigators
      this.$sdk.profile.list(builder => {
        builder.page = 1
        builder.limit = 1000
        builder.filters = { email: ['tigfreight.com'] }
        builder.order = 'name_full:asc'
      }).then(res => {
        this.investigators = res
        this.advisors = this.investigators
      })
      // ACCOUNT TYPE
      this.$sdk.dictionary_acctype.list(builder => {
        builder.page = 1
        builder.limit = 1000
        builder.order = 'uuid:asc'
      }).then(res => {
        this.accTypes = res
      })
      // STATIONARY TYPE
      this.$sdk.dictionary_stationarytype.list(builder => {
        builder.page = 1
        builder.limit = 1000
        builder.order = 'uuid:asc'
      }).then(res => {
        this.stationaryTypes = res
      })
      // ACCOUNT GRADE
      this.$sdk.dictionary_accgrade.list(builder => {
        builder.page = 1
        builder.limit = 1000
        builder.order = 'uuid:asc'
      }).then(res => {
        this.accGrades = res
      })
    },
    saveAccountEdit () {
      this.$refs.editForm.validate((valid) => {
        if (valid) {
          this.isLoading = true
          this.account.update()
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isLoading = false
              this.readAccount()
            })
        }
      })
    },
    async querySearchParentAccount (queryString, cb) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(async () => {
        if (queryString) {
          let parents = await this.$sdk.account.list(builder => {
            builder.page = 1
            builder.limit = 10
            builder.filters = { account_name: [queryString] }
            builder.order = 'name:asc'
          })
          cb(parents)
        } else {
          cb()
        }
      }, 600)
    },
    handleSelectParent (item) {
      this.account.parent = item
      this.parentInput = item.name
    },
    clearParentInput () {
      this.account.parent = null
      this.parentInput = ''
    },
    suburbQuerySearch (queryString, cb) {
      this.$sdk.dictionary_location.list(builder => {
        builder.page = 1
        builder.limit = 50
        builder.filters = { dictionary_location_find: [queryString] }
        builder.order = 'suburb:asc'
      }).then(res => {
        cb(res)
      })
    },
    handleSelectSuburb (item) {
      this.account.suburb = item.suburb
      this.account.state = item.state
      this.account.postcode = item.postcode
    },
    formatSuburbByLocation (location) {
      if (location) {
        return `${location.suburb}, ${location.state}, ${location.postcode}`
      } else return ''
    }
  }
}
</script>

<style>

</style>
